import { useState, useEffect } from "react";
import { useNavigate, Link, Outlet, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { 
  LayoutDashboard, Users, Image, Video, MessageSquare, Handshake, 
  Settings, LogOut, Menu, X, TrendingUp, IndianRupee, Clock, CheckCircle, Building2, Calendar
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_chennai-finrun/artifacts/eabq3wxl_FINRUN%20LOGO%20PNG%202.png";

// Create axios instance with auth
const getAuthAxios = () => {
  const auth = sessionStorage.getItem("adminAuth");
  return axios.create({
    headers: {
      Authorization: `Basic ${auth}`
    }
  });
};

const AdminLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    const auth = sessionStorage.getItem("adminAuth");
    if (!auth) {
      navigate("/admin");
      return;
    }
    fetchDashboard();
  }, [navigate]);

  const fetchDashboard = async () => {
    try {
      const authAxios = getAuthAxios();
      const response = await authAxios.get(`${API}/admin/dashboard`);
      setStats(response.data.stats);
    } catch (error) {
      if (error.response?.status === 401) {
        sessionStorage.removeItem("adminAuth");
        navigate("/admin");
      }
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem("adminAuth");
    toast.success("Logged out successfully");
    navigate("/admin");
  };

  const navItems = [
    { label: "Dashboard", icon: LayoutDashboard, path: "/admin/dashboard" },
    { label: "Registrations", icon: Users, path: "/admin/registrations" },
    { label: "Event Details", icon: Calendar, path: "/admin/events" },
    { label: "Gallery", icon: Image, path: "/admin/gallery" },
    { label: "Hero Video", icon: Video, path: "/admin/hero-video" },
    { label: "Partners & Logos", icon: Building2, path: "/admin/partners" },
    { label: "Sponsor Inquiries", icon: Handshake, path: "/admin/sponsors" },
    { label: "Contacts", icon: MessageSquare, path: "/admin/contacts" },
    { label: "Settings", icon: Settings, path: "/admin/settings" },
  ];

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="admin-layout">
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-[#111827] transform transition-transform duration-300 ${
        sidebarOpen ? "translate-x-0" : "-translate-x-full"
      } lg:translate-x-0`}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-white/10">
            <Link to="/">
              <img src={LOGO_URL} alt="The FinRun" className="h-14 w-auto" />
            </Link>
            <p className="text-gray-500 text-xs mt-2">Admin Panel</p>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                    isActive 
                      ? "bg-[#FF3B30] text-white" 
                      : "text-gray-400 hover:bg-white/5 hover:text-white"
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span className="font-medium">{item.label}</span>
                </Link>
              );
            })}
          </nav>

          {/* Logout */}
          <div className="p-4 border-t border-white/10">
            <Button 
              onClick={handleLogout}
              variant="ghost"
              className="w-full justify-start text-gray-400 hover:text-white hover:bg-white/5"
            >
              <LogOut className="w-5 h-5 mr-3" />
              Logout
            </Button>
          </div>
        </div>
      </aside>

      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-40 bg-white shadow-sm">
        <div className="flex items-center justify-between p-4">
          <button onClick={() => setSidebarOpen(!sidebarOpen)}>
            {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
          <img src={LOGO_URL} alt="The FinRun" className="h-12 w-auto" />
          <div className="w-6" />
        </div>
      </header>

      {/* Overlay */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 z-40 bg-black/50"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <main className="lg:ml-64 min-h-screen pt-16 lg:pt-0">
        {/* Stats Bar */}
        {stats && (
          <div className="bg-white border-b p-4">
            <div className="flex flex-wrap gap-4">
              <div className="flex items-center gap-2 px-4 py-2 bg-green-50 rounded-lg">
                <CheckCircle className="w-4 h-4 text-green-600" />
                <span className="text-sm font-medium text-green-700">{stats.paid_registrations} Paid</span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 bg-yellow-50 rounded-lg">
                <Clock className="w-4 h-4 text-yellow-600" />
                <span className="text-sm font-medium text-yellow-700">{stats.pending_registrations} Pending</span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 bg-blue-50 rounded-lg">
                <IndianRupee className="w-4 h-4 text-blue-600" />
                <span className="text-sm font-medium text-blue-700">₹{stats.total_revenue?.toLocaleString()} Revenue</span>
              </div>
            </div>
          </div>
        )}

        <div className="p-6">
          <Outlet context={{ stats, fetchDashboard, getAuthAxios }} />
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;
export { getAuthAxios };
